import React from 'react';

/**
 * ErrorBoundary - 에러 바운더리 컴포넌트
 *
 * 하위 컴포넌트의 렌더링 오류를 포착하여 전체 앱 크래시를 방지하고 복구 UI를 제공한다.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconWrapper}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="1.5"
              width="48"
              height="48"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 style={styles.title}>예기치 않은 오류가 발생했습니다</h1>
          <p style={styles.message}>페이지를 새로고침하거나 홈으로 이동해 주세요.</p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={styles.errorDetail}>{this.state.error.toString()}</pre>
          )}
          <div style={styles.actions}>
            <button style={styles.primaryButton} onClick={this.handleReload}>
              홈으로 이동
            </button>
            <button style={styles.secondaryButton} onClick={this.handleRetry}>
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '1rem',
  },
  card: {
    textAlign: 'center',
    maxWidth: '480px',
    width: '100%',
    padding: '3rem 2rem',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  iconWrapper: {
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1e293b',
    margin: '0 0 0.75rem 0',
  },
  message: {
    fontSize: '0.925rem',
    color: '#64748b',
    margin: '0 0 1.5rem 0',
    lineHeight: 1.6,
  },
  errorDetail: {
    textAlign: 'left',
    fontSize: '0.8rem',
    color: '#dc2626',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1.5rem',
    overflow: 'auto',
    maxHeight: '120px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
  },
  primaryButton: {
    padding: '0.625rem 1.5rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: '#2d3a52',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '0.625rem 1.5rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#475569',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

export default ErrorBoundary;
